import React, { useState, useEffect, useCallback } from 'react';
import Nav from '../Home/Nav/Nav.js';
import { FaLongArrowAltLeft, FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa"; 
import { Link as RouterLink } from 'react-router-dom';
import { HOST } from '../../../config.js';
import { useTranslation } from 'react-i18next';

const API_URL = HOST + 'get_gallery.php';
const IMAGE_BASE_URL = HOST + 'gallery_images/';

const Lightbox = ({ item, onClose, onPrev, onNext, hasPrev, hasNext }) => {
    
    if (!item) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4">
            
            <button 
                onClick={onClose} 
                className="absolute top-4 right-4 text-white text-2xl p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition duration-150 z-50"
            >
                <FaTimes />
            </button>
            
            <div className="relative flex items-center justify-center w-full h-full">
                
                {hasPrev && (
                    <button 
                        onClick={onPrev} 
                        className="absolute left-4 md:left-8 text-white text-4xl p-3 bg-black bg-opacity-50 rounded-full hover:bg-opacity-80 transition duration-150 z-10"
                    >
                        <FaChevronLeft />
                    </button>
                )}
                
                <img 
                    src={`${IMAGE_BASE_URL}${item.filename}`} 
                    alt={item.caption || 'Image agrandie'} 
                    className="max-w-full max-h-full object-contain shadow-2xl rounded-lg" 
                />

                {hasNext && (
                    <button 
                        onClick={onNext} 
                        className="absolute right-4 md:right-8 text-white text-4xl p-3 bg-black bg-opacity-50 rounded-full hover:bg-opacity-80 transition duration-150 z-10"
                    >
                        <FaChevronRight />
                    </button>
                )}
            </div>
            
            {item.caption && (
                <div className="absolute bottom-4 left-0 right-0 text-center text-white text-lg p-2 bg-black bg-opacity-50">
                    {item.caption}
                </div>
            )}
        </div>
    );
};


function Galerie () {
    const { t, i18n } = useTranslation();
  
      const changeLanguage = (e) => {
        i18n.changeLanguage(e.target.value);
    };
    const [galleryItems, setGalleryItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(-1); 

    const openLightbox = (index) => {
        setSelectedImageIndex(index);
    };

    const closeLightbox = useCallback(() => {
        setSelectedImageIndex(-1);
    }, []);

    const navigatePrev = useCallback(() => {
        setSelectedImageIndex(prevIndex => 
            (prevIndex - 1 + galleryItems.length) % galleryItems.length
        );
    }, [galleryItems.length]); // Added galleryItems.length as a dependency

    const navigateNext = useCallback(() => {
        setSelectedImageIndex(prevIndex => 
            (prevIndex + 1) % galleryItems.length
        );
    }, [galleryItems.length]); // Added galleryItems.length as a dependency
    
    // Now this hook is happy because the dependencies above are stable
    const handleKeyDown = useCallback((e) => {
        if (selectedImageIndex !== -1) {
            if (e.key === 'Escape') {
                closeLightbox();
            } else if (e.key === 'ArrowLeft') {
                navigatePrev();
            } else if (e.key === 'ArrowRight') {
                navigateNext();
            }
        }
    }, [
        selectedImageIndex,
        closeLightbox,
        navigatePrev,
        navigateNext
    ]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        const fetchImages = async () => {
            try {
                const response = await fetch(API_URL);
                if (!response.ok) {
                    throw new Error(`HTTP status: ${response.status}`);
                }
                const data = await response.json();
                
                if (data.success) {
                    setGalleryItems(data.images); 
                } else {
                    throw new Error(data.message || "Failed to fetch images (API success: false).");
                }
                
            } catch (err) {
                console.error("Failed to fetch images:", err);
                setError(err.message); 
            } finally {
                setIsLoading(false); 
            }
        };

        fetchImages();
    }, []); 
    
    if (isLoading) {
        return <div className="text-center pt-20">Chargement de la galerie...</div>;
    }
    if (error) {
        return <div className="text-center pt-20 text-red-600">Erreur de chargement: {error}.</div>;
    }
    
    const selectedItem = galleryItems[selectedImageIndex];
    const hasPrev = galleryItems.length > 1; 
    const hasNext = galleryItems.length > 1; 

    return (
        <div>
            <div className='Infos'>
                <div className='pt-10' ><Nav/></div>
                <div className='Infos_all'>
                    <div className='Infos_titles'>
                        <h1>{t('galerie.title')}</h1>
                    </div>
                    <div className='buttons'>
                        <RouterLink to="/" className='Infos_button2'>
                            <FaLongArrowAltLeft />
                            {t('galerie.button')}
                        </RouterLink>
                    </div>
                </div>
            </div>
            <div>
                <div className="container mx-auto p-4">
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        
                        {galleryItems.map((item, index) => (
                            <div 
                                key={item.id} 
                                className="bg-white shadow-lg rounded-lg cursor-pointer"
                                onClick={() => openLightbox(index)}
                            >
                                
                                <img 
                                    className="w-full h-64 object-cover transition duration-300 hover:scale-[1.02]" 
                                    src={`${IMAGE_BASE_URL}${item.filename}`} 
                                    alt={item.caption || 'Image de la galerie'} 
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <Lightbox 
                item={selectedItem} 
                onClose={closeLightbox} 
                onPrev={navigatePrev} 
                onNext={navigateNext} 
                hasPrev={hasPrev} 
                hasNext={hasNext} 
            />
        </div>
    )
}

export default Galerie;